import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ReferencePage } from "./referencePage";
import { ReferenceForm } from "./referenceForm";
import { ReferenceView } from "./referenceView";
import { ReferenceService } from "./referenceService";
import { GRID_ACTION_TYPES, GridActionType } from "../../../assets/types";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { Constants } from "../../../assets/utils/lib/constants";
import { IActionResponse } from "../../../assets/utils/lib";
import { isGridActionType } from "../../../assets/utils/helpers";

import { ReferenceDto } from "../../../services/dto";
import { LoadingIndicator } from "../../../assets/vfx";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

export const LINK_TYPE = {
  REFERENCE_ITEM: "6F5CF003-520B-F011-8EF4-00155D112802",
  EXPLORE_ITEM: "bd5270d9-fd68-f111-8ef8-00155d112802", //to be removed later when explore has it's own page and api backend for places and reviews
  SOCIAL_ITEM: "8EC42B30-520B-F011-8EF4-00155D112802",
  NAV_MENU_ITEM: "4fc73710-520b-f011-8ef4-00155d112802",
};

export const getTitle = (linkTypeId?: string): string => {
  const titles: Record<string, string> = {
    [LINK_TYPE.REFERENCE_ITEM]: "Home Tile",
    [LINK_TYPE.EXPLORE_ITEM]: "Explore", //to be removed later when explore has it's own page and api backend for places and reviews
    [LINK_TYPE.SOCIAL_ITEM]: "Social",
    [LINK_TYPE.NAV_MENU_ITEM]: "Nav Menu List",
  };
  return titles[linkTypeId ?? ""] || Constants.emptyString;
};

interface ReferenceContainerProps {
  linkTypeId: string;
}

export const ReferenceContainer = (props: ReferenceContainerProps) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [drawerType, setDrawerType] = useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const [data, setData] = useState<ReferenceDto>();
  const [communityId, setCommunityId] = useState<string>();

  const linkType: string = getTitle(props.linkTypeId);
  const isEditMode: boolean = !!(data?.id && data.id !== Constants.emptyGuid);

  const lookupQuery = ReferenceService.getLookups();
  const referenceQuery = ReferenceService.getReferenceQuery(
    data?.id ?? undefined
  );
  const referenceParentQuery = ReferenceService.getReferenceQuery(
    data?.parentId ?? undefined
  );
  const communityReferenceQuery =
    ReferenceService.getAllCommunityReferencesQuery(
      communityId ?? Constants.emptyGuid
    );

  const { mutateAsync: saveReference } = ReferenceService.save();
  const { mutate: deleteReference } = ReferenceService.deleteReference();

  const closeDrawer = (): void =>{
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }


    const refreshGrid = (): void => {
    isEditMode && referenceQuery.refetch();
    queryClient.invalidateQueries({ queryKey: ["referenceGridQuery"] });
    isEditMode && referenceParentQuery.refetch();
    communityReferenceQuery.refetch();
    queryClient.invalidateQueries({ queryKey: ["referenceGridQuery"] });
    closeDrawer();
  };

  const getSiblingsCount = (parentId?: string) => {
    const all = communityReferenceQuery.data ?? [];
    return all.filter(
      (r) =>
        (parentId ? r.parentId === parentId : !r.parentId) &&
        r.linkTypeId === props.linkTypeId
    ).length;
  };

  const handleApiResponse = (response: IActionResponse, successMessage: string): void => {
    const errorMsg: string | null = apiErrorMessage(response.errorMessages);
    showSnackbar(
      errorMsg || successMessage,
      errorMsg ? MessageSeverity.Error : MessageSeverity.Success
    );
    if (!errorMsg) refreshGrid();
  };

  const handleSubmit = async (values: ReferenceDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    values.linkTypeId = props.linkTypeId;

    const message: string = isEditMode
      ? `${linkType} updated`
      : `${linkType} saved`;
    return await saveReference(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleDelete = (): void => {
    deleteReference(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) =>
        handleApiResponse(response, `${linkType} deleted`),
    });
  };

    const handleCustomEvent = (actionType: string, dataId: string): void => {
    if (!isGridActionType(actionType)) return;
    setDrawerType(actionType);
    setData(
      (actionType === GRID_ACTION_TYPES.NEW_SIBLING
        ? { parentId: dataId }
        : { id: dataId }) as ReferenceDto
      );
      setSearchAction(actionType, dataId);
  };

    const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);

  const handleEdit = (): void => {
      setData(referenceQuery.data);
      setDrawerType(GRID_ACTION_TYPES.EDIT);
      if (referenceQuery.data?.id) {
          setSearchAction(GRID_ACTION_TYPES.EDIT, referenceQuery.data?.id);
      }
  };

  const getDrawerContentProps = () => ({
    show: true,
    communityId,
    linkType,
    linkTypeId: props.linkTypeId,
    lookups: {
      communities: lookupQuery.data?.communities ?? [],
      pageLinks: lookupQuery.data?.pageLink ?? [],
    },
    showProxy: props.linkTypeId === LINK_TYPE.NAV_MENU_ITEM,
    onHide: closeDrawer,
    onSubmit: handleSubmit,
  });

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
      case GRID_ACTION_TYPES.NEW_SIBLING:
        const parentIdForNew = data?.parentId;
        const totalForNew = getSiblingsCount(parentIdForNew);
        return (
          <ReferenceForm
            {...getDrawerContentProps()}
            parentId={data?.parentId}
            totalNumberOfItems={totalForNew}
            references={communityReferenceQuery.data ?? []}
            setCommunityId={setCommunityId}
            parent={referenceParentQuery?.data}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        const parentIdForEdit = referenceQuery.data?.parentId;
        const totalForEdit = getSiblingsCount(parentIdForEdit);
        return (
          <ReferenceForm
            {...getDrawerContentProps()}
            show={!!referenceQuery.data}
            data={referenceQuery.data}
            totalNumberOfItems={totalForEdit}
            loading={referenceQuery.isLoading}
            parentId={referenceQuery.data?.parentId}
            parent={referenceParentQuery?.data}
            references={communityReferenceQuery.data ?? []}
            setCommunityId={setCommunityId}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
        return (
          <ReferenceView
            data={referenceQuery.data}
            show={!!referenceQuery.data}
            linkType={linkType}
            pageLinks={lookupQuery.data?.pageLink ?? []}
            references={communityReferenceQuery.data ?? []}
            onHide={closeDrawer}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {lookupQuery?.isLoading ? (
        <LoadingIndicator isFullPage={true} />
      ) : (
        <ReferencePage
          communities={lookupQuery.data?.communities ?? []}
          linkTypeId={props.linkTypeId}
          setCommunityId={setCommunityId}
          onCustomEvent={handleCustomEvent}
        />
      )}
      {renderDrawerContent()}
    </>
  );
};
